import layouts from './ulFormLayouts.json';

export const fieldKey = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
export const isTrue = (value) =>
  value === true ||
  value === 1 ||
  ['true', '1', 'yes'].includes(String(value).trim().toLowerCase());
export const isControlField = (field) =>
  ['isactive', 'islocked'].includes(fieldKey(field.FieldName));

export const inputType = (fieldType, fallback = 'text') => {
  const type = fieldKey(fieldType);
  if (!type) return fallback;
  if (['boolean', 'checkbox', 'check'].includes(type)) return 'check';
  if (type === 'longtext') return 'textarea';
  if (type === 'string') return 'text';
  return ['yesna', 'yesno', 'text', 'number', 'date', 'textarea'].includes(type)
    ? type
    : fallback;
};

export function buildULForms(fields, formLayouts = layouts) {
  const groups = new Map();
  fields.forEach((field) => {
    const key =
      field.FormId != null ? `id:${field.FormId}` : `name:${field.FormName}`;
    groups.set(key, [...(groups.get(key) || []), field]);
  });
  return formLayouts.map((layout) => {
    const formFields =
      [...groups.values()].find(
        (group) => matchULLayout(group, formLayouts)?.id === layout.id,
      ) || [];
    const used = new Set();
    const missing = [];
    const sections = layout.sections.map((section) => ({
      ...section,
      fields: section.fields
        .flatMap((definition) => {
          const field = formFields.find(
            (item) => fieldKey(item.FieldName) === fieldKey(definition.name),
          );
          if (!field) {
            missing.push(definition.name);
            return [];
          }
          used.add(field.Id);
          return [
            {
              ...definition,
              ...field,
              type: inputType(field.FieldType, definition.type),
              label: definition.label || field.FieldName,
            },
          ];
        })
        .sort((a, b) => a.SortOrder - b.SortOrder),
    }));
    const additional = formFields
      .filter((field) => !used.has(field.Id) && !isControlField(field))
      .sort((a, b) => a.SortOrder - b.SortOrder)
      .map((field) => ({
        ...field,
        label: field.FieldName,
        type: inputType(field.FieldType),
      }));
    if (additional.length)
      sections.splice(sections.length - 2, 0, {
        title: 'Additional fields',
        layout: 'grid',
        fields: additional,
      });
    const activeField = formFields.find(
      (field) => fieldKey(field.FieldName) === 'isactive',
    );
    const lockedField = formFields.find(
      (field) => fieldKey(field.FieldName) === 'islocked',
    );
    return {
      ...layout,
      ...(formFields.length &&
      fieldKey(formFields[0].FormName) !== fieldKey(layout.formName)
        ? {
            title: formFields[0].FormName.replace(/^UL\s*-\s*/i, ''),
            formName: formFields[0].FormName,
            subtitle: '',
          }
        : {}),
      sections,
      fields: sections.flatMap((section) => section.fields),
      activeField,
      lockedField,
      missing,
    };
  });
}

// Identify renamed UL forms by their checklist fields instead of hard-coded database IDs.
export function matchULLayout(fields, formLayouts = layouts) {
  const exact = formLayouts.find((layout) =>
    fields.some(
      (field) => fieldKey(field.FormName) === fieldKey(layout.formName),
    ),
  );
  if (exact) return exact;
  const names = new Set(fields.map((field) => fieldKey(field.FieldName)));
  const candidates = formLayouts
    .map((layout) => {
      const definitions = layout.sections.flatMap((section) => section.fields);
      return {
        layout,
        score:
          definitions.filter((field) => names.has(fieldKey(field.name)))
            .length / definitions.length,
      };
    })
    .sort((a, b) => b.score - a.score);
  return candidates[0]?.score >= 0.5 ? candidates[0].layout : null;
}

export const valueText = (value) => (value == null ? '' : String(value));
export const defaultValue = (field) => (field.type === 'check' ? 'false' : '');

export const isFormComplete = (form, saved, draft = {}) => form.fields.every((field) => {
  const value = draft[field.Id] ?? saved[field.Id]?.FieldValue ?? '';
  return inputType(field.FieldType, field.type) === 'check'
    ? isTrue(value)
    : valueText(value).trim() !== '';
});

export function normalizeValues(data, projectId) {
  if (!Array.isArray(data))
    throw new Error('The form values API did not return a list.');
  const values = {};
  for (const item of data) {
    const project = item.ProjectId ?? item.projectId ?? item.ProjectID;
    if (String(project) !== String(projectId)) continue;
    const fieldId = item.FieldId ?? item.fieldId ?? item.FieldID;
    const Id = item.Id ?? item.id ?? item.ID;
    if (fieldId == null || Id == null)
      throw new Error('A saved form value is missing its identifier.');
    if (values[fieldId])
      throw new Error(
        `Multiple values exist for field ${fieldId} on this project. Resolve the duplicate before editing.`,
      );
    values[fieldId] = {
      Id,
      ProjectId: Number(project),
      FieldId: fieldId,
      FieldValue: valueText(item.FieldValue ?? item.fieldValue),
    };
  }
  return values;
}

export async function readFormValues(request, url, projectId) {
  return normalizeValues(
    await request({ url, params: { projectId: Number(projectId) } }),
    projectId,
  );
}

export async function writeFormValue(
  request,
  url,
  projectId,
  fieldId,
  value,
  saved,
) {
  const previous = saved[fieldId];
  const data = {
    ...(previous ? { Id: previous.Id } : {}),
    ProjectId: Number(projectId),
    FieldId: fieldId,
    FieldValue: valueText(value),
  };
  await request({
    url,
    method: previous ? 'PUT' : 'POST',
    data,
    contract: { requestCase: 'raw' },
  });
}

// Reconcile with the server on every action, including retries after partial saves.
// Lock only after a read confirms every submitted field was persisted.
export async function saveULForm({
  request,
  url,
  projectId,
  form,
  draft,
  submit,
  onSnapshot,
  onLocked,
  onlyDraft = false,
}) {
  let saved = await readFormValues(request, url, projectId);
  onSnapshot(saved);
  if (isTrue(saved[form.lockedField.Id]?.FieldValue))
    throw new Error('This form has already been submitted and is locked.');
  if (!isTrue(saved[form.activeField.Id]?.FieldValue))
    throw new Error('This form is no longer active. Refresh to reload it.');
  if (submit && form.requireComplete && !isFormComplete(form, saved, draft))
    throw new Error('Complete every field and check every checklist item before submitting.');
  const expected = {};
  const fields = onlyDraft
    ? form.fields.filter((field) =>
        Object.prototype.hasOwnProperty.call(draft, field.Id),
      )
    : form.fields;
  for (const field of fields) {
    const value =
      draft[field.Id] ??
      valueText(saved[field.Id]?.FieldValue ?? defaultValue(field));
    expected[field.Id] = valueText(value);
    if (
      !saved[field.Id] ||
      valueText(saved[field.Id].FieldValue) !== valueText(value)
    ) {
      await writeFormValue(request, url, projectId, field.Id, value, saved);
    }
  }
  saved = await readFormValues(request, url, projectId);
  onSnapshot(saved);
  for (const field of fields) {
    if (
      !saved[field.Id] ||
      valueText(saved[field.Id].FieldValue) !== expected[field.Id]
    ) {
      throw new Error(
        'Some values could not be verified. Your draft has been kept; save again before submitting.',
      );
    }
  }
  if (submit && !isTrue(saved[form.lockedField.Id]?.FieldValue)) {
    await writeFormValue(
      request,
      url,
      projectId,
      form.lockedField.Id,
      'true',
      saved,
    );
    onLocked();
    saved = await readFormValues(request, url, projectId);
    if (!isTrue(saved[form.lockedField.Id]?.FieldValue))
      throw new Error(
        'The submission lock could not be verified. Refresh to check the saved status.',
      );
    onSnapshot(saved);
  }
  return saved;
}
